import { ILeadSource } from '@bridebook/models/source/models/Suppliers/Leads.types';
import type { EnquiryDetail } from '@bridebook/web/lib/enquiries/enquiry-readiness';
import type {
  SendEnquiryInput,
  SendEnquiryResult,
} from '@bridebook/web/lib/enquiries/hooks/use-send-enquiry';
import {
  LIVE_27666_VENUE_INTENT_OPTIONS,
  resolveLIVE_27666_InitialIntent,
  type EnquiryIntents,
} from '@bridebook/web/lib/enquiries/intent';
import { EnquiryContext, type EnquiryFormInfoProps } from '@bridebook/web/lib/enquiries/types';
import { deliveredSupplierIds } from '@bridebook/web/lib/enquiries/utils/enqueue-enquiry';
import { PLANNER_ANALYTICS_SOURCE } from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';

// The analytics section of a form the card opened: Review, or Approve on a record that lacks details.
export type PlannerContactSection = 'approve' | 'review' | 'updateDetails';
export type EnquiryFormSection = 'details' | 'guests' | 'date';

/**
 * An enquiry form opened from a planner proposal, on the proposed message.
 * `section` is where it opens while the couple's record lacks a detail.
 */
export type EnquiryPlannerOrigin = {
  toolCallId: string;
  message: string;
  // The intent the coach read off the couple's words: the form's pills open on it.
  intent: EnquiryIntents;
  section?: EnquiryFormSection;
};

export type PlannerEnquiryOutcome =
  | {
      ok: true;
      data: { outcome: 'sent'; suppliers: string[]; failed?: string[]; message: string };
    }
  | { ok: false; error: string };

/**
 * What a form opened from a proposal tells the planner: a send is in flight,
 * so the proposal cannot be sent twice; nothing was sent; or the real outcome.
 */
export type EnquiryPlannerResult = { toolCallId: string } & (
  | { status: 'sending' }
  | { status: 'aborted' }
  | ({ status: 'settled' } & PlannerEnquiryOutcome)
);

// The intents the coach may name: the pills the enquiry form shows for a venue.
export type PlannerEnquiryIntent = (typeof LIVE_27666_VENUE_INTENT_OPTIONS)[number];

/**
 * The intent a planner enquiry sends with, from the keys the coach inferred,
 * resolved as the form resolves a remembered choice: only the pills shown for
 * this supplier type count, and with none left the type's default applies.
 */
export const plannerEnquiryIntent = (
  keys: readonly PlannerEnquiryIntent[] | undefined,
  isVenue: boolean,
): EnquiryIntents =>
  resolveLIVE_27666_InitialIntent({
    isVenue,
    cachedIntent: Object.fromEntries((keys ?? []).map((key) => [key, true])),
  }).intent;

// What every planner enquiry sends with, whichever button sent it.
export const PLANNER_SEND_INPUT = {
  noValidation: true,
  noConfirmation: true,
  source: ILeadSource.Concierge,
  // Sent as the contact location of the Emailed Supplier event
  contactLocation: PLANNER_ANALYTICS_SOURCE,
} satisfies Partial<SendEnquiryInput>;

/**
 * What the coach is told about a send. Only a delivered enquiry is a success:
 * the pipeline also resolves on the enquiry limit and on a spam hold, and a
 * batch can fail for some venues.
 */
export const plannerEnquiryOutcome = (result: SendEnquiryResult): PlannerEnquiryOutcome => {
  const names = result.contactedSuppliers.map((supplier) => supplier.name);
  const notContacted = `Venues not contacted: ${names.join(', ')}`;
  if (result.outcome === 'limit') {
    return {
      ok: false,
      error: `Not sent: the couple has reached their enquiry limit for this supplier category. Tell them, and do not retry. ${notContacted}`,
    };
  }
  if (result.outcome === 'spam') {
    return {
      ok: false,
      error: `Not sent: the message was held by the spam check. Say it could not be sent and offer to reword it. ${notContacted}`,
    };
  }
  const delivered = deliveredSupplierIds(result.response);
  const sent = result.contactedSuppliers.filter((supplier) => delivered.has(supplier.id));
  const failed = result.contactedSuppliers.filter((supplier) => !delivered.has(supplier.id));
  if (sent.length === 0) {
    return {
      ok: false,
      error: `Not sent: the enquiry could not be delivered. Say it failed and offer to try again. ${notContacted}`,
    };
  }
  return {
    ok: true,
    data: {
      outcome: 'sent',
      suppliers: sent.map((supplier) => supplier.name),
      ...(failed.length > 0 ? { failed: failed.map((supplier) => supplier.name) } : {}),
      message: result.message,
    },
  };
};

// What the coach is told when the app cannot send to the venue at all.
export const plannerCannotContactError = (
  name: string,
  reason: 'email' | 'conversation',
): string =>
  reason === 'conversation'
    ? `Not sent: the couple already has a conversation with ${name} in their Bridebook inbox, so no new enquiry went. Do not retry. Say so in one plain sentence and that they can carry on that conversation in their inbox; the app shows them a button to it, so say nothing else about the inbox. If they asked to contact other venues too, go on with the next one. Otherwise end on the outcome; when the turn note allows an offer, you may offer to find similar venues they have not contacted, never one about ${name} itself.`
    : `Not sent: ${name} cannot be contacted through Bridebook. Say so plainly; do not retry.`;

// The result of an enquiry call that gets no card: the couple answers one card at a time.
export const plannerBlockedEnquiryError = (name: string, reason: 'open' | 'answered'): string =>
  reason === 'open'
    ? `Not sent yet: the couple answers one enquiry card at a time, and another card in this reply is still open. Do not call it again now: once that card is settled, the turn note says when to go on with ${name}.`
    : `Not sent: ${name} already had its enquiry card in this reply, and the couple answered it. Do not call it again in this reply.`;

export const plannerInfoProps = (contactSection: PlannerContactSection): EnquiryFormInfoProps => ({
  method: PLANNER_ANALYTICS_SOURCE,
  contactLocation: PLANNER_ANALYTICS_SOURCE,
  contactSection,
  cta: 'Request a brochure',
  context: EnquiryContext.Brochure,
});

// The card button that opened the form. Approve opens it only when details are missing.
const TRIGGERED_FORM_CTA: Record<PlannerContactSection, 'Approve' | 'Review'> = {
  approve: 'Approve',
  updateDetails: 'Approve',
  review: 'Review',
};

// A section the planner's enquiry card opens the form with. Other planner forms have their own.
const isPlannerContactSection = (section: unknown): section is PlannerContactSection =>
  typeof section === 'string' && Object.hasOwn(TRIGGERED_FORM_CTA, section);

// The cta for a Triggered enquiry form event, when the planner's enquiry card opened the form.
export const plannerTriggeredFormCta = (infoProps?: EnquiryFormInfoProps) =>
  infoProps?.contactLocation === PLANNER_ANALYTICS_SOURCE &&
  isPlannerContactSection(infoProps.contactSection)
    ? TRIGGERED_FORM_CTA[infoProps.contactSection]
    : undefined;

const SECTION_OF: Record<EnquiryDetail, EnquiryFormSection> = {
  partnerNames: 'details',
  email: 'details',
  phone: 'details',
};

export const enquiryFormSectionOf = (detail: EnquiryDetail): EnquiryFormSection =>
  SECTION_OF[detail];

// The message a proposal's form opens on: the couple's own edit of it, else the proposal.
export const plannerDraftMessage = (
  draft: { toolCallId: string; message: string } | undefined,
  origin: EnquiryPlannerOrigin,
): string => (draft?.toolCallId === origin.toolCallId ? draft.message : origin.message);
